import { Plus } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Panel } from "@/components/ui/Panel";
import { Spinner } from "@/components/ui/Spinner";
import { toast } from "sonner";
import { useClientFromList, useDeleteClient } from "@/hooks/use-clients";
import { refusalDetail } from "@/services/api-client";
import type { ClientList, ClientSummary } from "@patent-studio/api-types";
import { Menu, MenuItem, MenuSeparator } from "@/components/ui/Menu";
import { AddClientDialog } from "./AddClientDialog";
import { ClientCard, UnassignedCard } from "./ClientCard";
import { DeleteClientDialog } from "./DeleteClientDialog";
import { EditClientDialog } from "./EditClientDialog";
import { KeyContactPopover } from "./KeyContactPopover";
import { ClientsPageFrame } from "./ClientsLayout";
import { StagePage } from "../layout/StagePage";

/** The acts on a client that are not opening it, behind "Options" in the card's corner. */
function ClientOptions({
  client,
  onEdit,
  onDelete,
}: {
  client: ClientSummary;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Menu name={`Options for ${client.name}`}>
      <MenuItem
        title="Edit client details"
        description="The name, key contact, industry and logo."
        onSelect={onEdit}
      />
      <MenuSeparator />
      <MenuItem
        danger
        title="Delete client"
        description="Removes the client and every draft under it, for good."
        onSelect={onDelete}
      />
    </Menu>
  );
}

/** Which client a dialog is open over, by id, so it always reads the client as the list now serves it. */
type Open = { kind: "edit" | "delete"; clientId: string } | null;

/** Deletes a client from the page, which outlives the dialog, so the answer is always told. */
function useDeleteFromPage() {
  const remove = useDeleteClient();
  return (client: ClientSummary) => {
    // One cascade at a time: a second confirm while one runs would start another.
    if (remove.isPending) return;
    remove.mutate(client.client_id, {
      onSuccess: () => {
        toast.success(`${client.name} was deleted.`);
      },
      onError: (error) => {
        toast.error(refusalDetail(error, "The client could not be deleted."));
      },
    });
  };
}

/** The dialog open over one client, reading the client as the list now serves it. */
function ClientDialog({
  opened,
  client,
  onClose,
}: {
  opened: Open;
  client: ClientSummary | undefined;
  onClose: () => void;
}) {
  const deleteClient = useDeleteFromPage();
  if (opened === null || client === undefined) return null;
  return opened.kind === "edit" ? (
    <EditClientDialog
      key={client.client_id}
      client={client}
      onClose={onClose}
    />
  ) : (
    <DeleteClientDialog
      client={client}
      onClose={onClose}
      onConfirm={() => {
        deleteClient(client);
      }}
    />
  );
}

/** The cards, each with its key contact and its options, and the drafts under no client last. */
function ClientCards({
  list,
  onLogoBroken,
  onOpen,
}: {
  list: ClientList;
  onLogoBroken: () => void;
  onOpen: (opened: Open) => void;
}) {
  const unassigned = list.unassigned_drafts;
  const hasUnassigned = unassigned.in_progress + unassigned.completed > 0;
  if (list.clients.length === 0 && !hasUnassigned) {
    return (
      <EmptyState>
        No clients yet. Add one, and start its first patent draft from its page.
      </EmptyState>
    );
  }
  return (
    <ul className="flex flex-col gap-2.5" aria-label="Clients">
      {list.clients.map((client) => (
        <ClientCard
          key={client.client_id}
          client={client}
          onLogoBroken={onLogoBroken}
          contact={
            <KeyContactPopover
              client={client}
              onEdit={() => {
                onOpen({ kind: "edit", clientId: client.client_id });
              }}
            />
          }
          corner={
            <ClientOptions
              client={client}
              onEdit={() => {
                onOpen({ kind: "edit", clientId: client.client_id });
              }}
              onDelete={() => {
                onOpen({ kind: "delete", clientId: client.client_id });
              }}
            />
          }
        />
      ))}
      {hasUnassigned && <UnassignedCard counts={unassigned} />}
    </ul>
  );
}

/**
 * Your clients: the page the application opens on. One card per client, newest
 * first, and at the foot one more for the caller's drafts under no client when
 * there are any.
 */
export function ClientsPage() {
  const [opened, setOpened] = useState<Open>(null);
  const clients = useClientFromList(opened?.clientId);
  const [adding, setAdding] = useState(false);
  // Joins a read already in flight rather than restarting it: every card's logo
  // link dies at once when the signing credentials rotate.
  const refetch = () => {
    void clients.refetch({ cancelRefetch: false });
  };

  return (
    <ClientsPageFrame crumbs={[{ label: "Your clients" }]}>
      <StagePage
        guide="home"
        testId="clients-page"
        title="Your clients"
        lede="Select a client to see their patent drafts, in progress and completed."
      >
        <div className="mt-8 mb-[26px]">
          <Button
            size="lead"
            onClick={() => {
              setAdding(true);
            }}
          >
            <Plus aria-hidden />
            New client
          </Button>
        </div>
        {clients.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner size="sm" />
            Loading your clients…
          </p>
        ) : clients.data === undefined ? (
          <Panel
            tone="destructive"
            className="flex items-center justify-between gap-3 text-sm"
          >
            Your clients could not be loaded.
            <Button variant="secondary" onClick={refetch}>
              Try again
            </Button>
          </Panel>
        ) : (
          <ClientCards
            list={clients.data}
            onLogoBroken={refetch}
            onOpen={(next) => {
              setOpened(next);
              // The cached list may predate work started since; the delete's warning is
              // read off it, so it is read again as the dialog opens.
              if (next?.kind === "delete") void clients.refetch();
            }}
          />
        )}
        <ClientDialog
          opened={opened}
          client={clients.client}
          onClose={() => {
            setOpened(null);
          }}
        />
        <AddClientDialog
          open={adding}
          onClose={() => {
            setAdding(false);
          }}
        />
      </StagePage>
    </ClientsPageFrame>
  );
}
